import { AssetClassName } from '@api/api/entity-constants';
import { ScenariosCondition, ScenariosResponse } from '@api/api/scenario';
import { mapAssetClass } from '@api/mappers/mapAssetClass';
import { Button } from '@design/components/button/Button';
import { Drawer } from '@design/components/drawer/Drawer';
import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { FilterList } from '@terminal/widgets/scenario/components/filter-list/FilterList';
import { ScenarioPriceMovement } from '@terminal/widgets/scenario/components/scenario-price-movement/ScenarioPriceMovement';
import { ScenarioQuality } from '@terminal/widgets/scenario/components/scenario-quality/ScenarioQuality';
import { useTranslation } from 'react-i18next';

import { MappedConditions } from '../scenario-assistant-modal/use-scenario-assistant/enrichedConditions';
import { ScenarioPastEpisodeSection } from '../scenario-past-episode-section/ScenarioPastEpisodeSection';
import styles from './ScenarioDrawer.module.scss';
import { generateLastConditionLabel } from './utils/utils';

export interface ScenarioDrawerProps {
  activeHorizon: string;
  closeDrawer: () => void;
  horizons: string[];
  isOpen?: boolean;
  isSummary: boolean;
  lastConditions?: ScenariosCondition[] | MappedConditions[];
  scenario: ScenariosResponse;
  entityTag: string;
}

export const ScenarioDrawer = ({
  activeHorizon,
  closeDrawer,
  horizons,
  isOpen = true,
  isSummary,
  lastConditions = [],
  entityTag,
  scenario,
}: ScenarioDrawerProps) => {
  const { t } = useTranslation(['scenario', 'widget', 'chart']);
  const { navigateToPage } = useNavigateToAssetOverview();
  const { data: entityData } = useEntity(entityTag);

  const entity = entityData?.data;
  const isFixedIncome =
    !!entity && mapAssetClass(entity).asset_class === AssetClassName.Fi;
  const bestHorizon = scenario.report.best_horizon;
  const initHorizon = isSummary ? bestHorizon : activeHorizon;
  const headerTitle = t('scenario:drawer.drawerTitle', {
    entityName: entity?.name ?? '',
    count: scenario.episodes.length,
  });

  const filterNames = lastConditions.map(condition => {
    if (!!condition.display) {
      return condition.display;
    } else if (!!(condition as MappedConditions).label) {
      return (condition as MappedConditions).label!;
    } else {
      return generateLastConditionLabel({
        t,
        conditions: [condition as MappedConditions],
      });
    }
  });

  let snakes = lastConditions.map(condition => condition.snake);

  const entitySnakePrice = `${entityTag}.price`;

  // Don't display the asset price chart twice
  if (!snakes.some(s => s === entitySnakePrice)) {
    snakes.unshift(entitySnakePrice);
  }

  const navigateToAssetOverview = () => {
    navigateToPage({
      entityTag,
      source: POSTHOG_TRACKING_SOURCES.SCENARIO_DRAWER,
      replace: true,
    });
  };

  return (
    <Drawer
      className={styles.styledDrawer}
      data-testid="scenario-drawer"
      isOpen={isOpen}
      handleCloseClick={closeDrawer}
      headerButton={
        <Button
          iconName="Expand"
          label={t('widget:assetOverview.assetOverview')}
          onClick={navigateToAssetOverview}
          variant="empty"
          size="xsmall"
          data-testid="asset-overview-button"
        />
      }
    >
      <DrawerContentWrapper>
        <div className={styles.scenarioDrawerWrapper}>
          <div className={styles.header}>
            <h2 className={styles.title}>{headerTitle}</h2>
            <div className={styles.subtitle}>
              <FilterList list={filterNames} />
            </div>
          </div>
          <ScenarioPriceMovement
            bestHorizon={bestHorizon}
            horizons={horizons}
            isFixedIncome={isFixedIncome}
            scenario={scenario}
          />
          <div className={styles.separator} />
          {scenario.episodes.length > 0 && (
            <ScenarioPastEpisodeSection scenario={scenario} snakes={snakes} />
          )}
          <div className={styles.lineBreak} />
          <ScenarioQuality
            bestHorizon={bestHorizon}
            horizons={horizons}
            initHorizon={initHorizon}
            isFixedIncome={isFixedIncome}
            scenario={scenario}
          />
        </div>
      </DrawerContentWrapper>
    </Drawer>
  );
};
